import { SearchBar } from "@dmca/frontend/components/input";
import { mergeSx, useGlobalContext } from "@dmca/frontend/hooks";
import {
  alpha,
  Box,
  CircularProgress,
  Container,
  Divider,
  Drawer,
  Fab,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  SxProps,
  Toolbar,
  type Theme,
} from "@mui/material";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { useMemo, useRef, useState } from "react";
import {
  DrawerLayoutContext,
  DrawerLayoutContextType,
  type DrawerLayoutListItemType,
} from "./-DrawerLayoutContext";
import MenuOpen from "@mui/icons-material/MenuOpen";

export const Route = createFileRoute("/_authed/_drawerLayout")({
  component: RouteComponent,
});

function RouteComponent() {
  const [searchInput, setSearchInput] = useState("");
  const [listItems, setListItems] = useState<DrawerLayoutListItemType>([]);
  const [userSx, setUserSx] = useState<SxProps<Theme>>();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState<
    DrawerLayoutListItemType[number] | undefined
  >();
  const [showSearchBar, setShowSearchBar] = useState(false);

  const onItemSelectedRef: DrawerLayoutContextType<DrawerLayoutListItemType>["onItemSelected"] =
    useRef(() => {});
  const itemFilterFuncRef: DrawerLayoutContextType<DrawerLayoutListItemType>["itemFilterFunc"] =
    useRef(() => true);

  const { isDesktop } = useGlobalContext();
  const [drawerIsOpen, setDrawerIsOpen] = useState(isDesktop);

  const onSelected = (item: DrawerLayoutListItemType[number]) => {
    setSelectedItem(item);
    onItemSelectedRef.current(item);
  };

  const parsedListItems = useMemo(() => {
    return listItems.filter((item) =>
      itemFilterFuncRef.current(item, searchInput)
    );
  }, [listItems, searchInput]);

  const drawerWidth = 200;

  // return
  return (
    <DrawerLayoutContext.Provider
      value={{
        searchTerm: [searchInput, setSearchInput],
        showSearchBar: [showSearchBar, setShowSearchBar],
        listItems: [listItems, setListItems],
        selectedItem: [selectedItem, setSelectedItem],
        sx: [userSx, setUserSx],
        onItemSelected: onItemSelectedRef,
        isLoading: [isLoading, setIsLoading],
        itemFilterFunc: itemFilterFuncRef,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexGrow: 1,
          flexDirection: isDesktop ? "row" : "column",
          height: isDesktop ? "auto" : "80vh",
          "& > .MuiContainer-root, & > .MuiDrawer-root": {
            ...(!isDesktop && { height: "80vh", overflow: "auto" }),
          },
        }}
      >
        <Drawer
          sx={mergeSx(
            {
              width: drawerWidth,
              maxHeight: "inherit",
              flexShrink: 0,
              "& .MuiDrawer-paper": {
                width: drawerWidth,
                zIndex: 0,
                maxHeight: "inherit",
                position: "relative",
                boxSizing: "border-box",
                ...(isDesktop && {
                  height: "auto",
                  overflowY: "visible",
                }),
              },
            },
            userSx
          )}
          anchor="left"
          variant={isDesktop ? "permanent" : "temporary"}
          open={drawerIsOpen}
          onClose={() => setDrawerIsOpen(false)}
        >
          {showSearchBar && (
            <>
              <Toolbar
                sx={{
                  px: "8px !important",
                }}
              >
                <SearchBar
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </Toolbar>
              <Divider />
            </>
          )}
          <Box
            sx={{
              width: "100%",
              height: "100%",
              position: "relative",
            }}
          >
            {isLoading && (
              <Box
                sx={(theme) => ({
                  display: "flex",
                  width: "100%",
                  height: "100%",
                  justifyContent: "center",
                  alignItems: "center",
                  position: "absolute",
                  zIndex: 100,
                  backgroundColor: alpha(theme.palette.background.default, 0.8),
                })}
              >
                <CircularProgress />
              </Box>
            )}
            <List data-testid="drawer-project-list">
              {parsedListItems.map((item) => {
                const id = typeof item === "string" ? item : item.label;
                const itemKey =
                  typeof item === "string" ? item : String(item.value);
                const isOverallReports = id === "Overall Reports";

                return (
                  <ListItem key={itemKey} disablePadding>
                    <ListItemButton
                      data-testid={
                        isOverallReports
                          ? "drawer-overall-reports-item"
                          : "drawer-project-item"
                      }
                      onClick={() => onSelected(item)}
                      selected={
                        selectedItem
                          ? itemKey ===
                            (typeof selectedItem === "string"
                              ? selectedItem
                              : String(selectedItem.value))
                          : false
                      }
                    >
                      {typeof item !== "string" && item.status && (
                        <Box
                          component="span"
                          role="img"
                          aria-label={item.status.label}
                          title={item.status.label}
                          sx={{
                            flexShrink: 0,
                            width: 10,
                            height: 10,
                            mr: 1,
                            borderRadius: "50%",
                            bgcolor:
                              item.status.color === "grey"
                                ? "grey.500"
                                : `${item.status.color}.main`,
                          }}
                        />
                      )}
                      <ListItemText
                        primary={id}
                        secondary={
                          typeof item === "string" ? undefined : item.secondary
                        }
                        slotProps={{
                          primary: { sx: { overflowWrap: "anywhere" } },
                          secondary: { sx: { overflowWrap: "anywhere" } },
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        </Drawer>
        <Container
          maxWidth="xl"
          disableGutters={!isDesktop}
          sx={{ minWidth: 0 }}
        >
          <Outlet />
        </Container>
      </Box>
      {!isDesktop && (
        <Fab
          sx={{
            position: "absolute",
            bottom: 20,
            left: 20,
          }}
          onClick={() => setDrawerIsOpen((prev) => !prev)}
        >
          <MenuOpen />
        </Fab>
      )}
    </DrawerLayoutContext.Provider>
  );
}
